<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminK8sDomainListItem, type PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminK8sDomains' })

const router = useRouter()

const loading = ref(true)
const domains = ref<AdminK8sDomainListItem[]>([])
const searchQuery = ref('')
const searchDebounce = ref<ReturnType<typeof setTimeout> | null>(null)
const verifiedFilter = ref<string>('all')

const pagination = usePagination({ initialItemsPerPage: 50 })

async function fetchDomains() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  let url = `/admin/k8s/domains?limit=${limit}&offset=${offset}`
  if (searchQuery.value.trim()) {
    url += `&search=${encodeURIComponent(searchQuery.value.trim())}`
  }
  if (verifiedFilter.value === 'verified') {
    url += '&verified=true'
  } else if (verifiedFilter.value === 'pending') {
    url += '&verified=false'
  }

  try {
    const res = await useAxios<null, PaginatedResponse<AdminK8sDomainListItem>>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminK8sDomainListItem.array().safeParse(res.data.data)
      if (validate.success) {
        domains.value = validate.data
      } else {
        domains.value = res.data.data as AdminK8sDomainListItem[]
      }
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sDomains' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, () => {
  fetchDomains()
})

watch(verifiedFilter, () => {
  pagination.goToPage(1)
  fetchDomains()
})

watch(searchQuery, () => {
  if (searchDebounce.value) {
    clearTimeout(searchDebounce.value)
  }
  searchDebounce.value = setTimeout(() => {
    pagination.goToPage(1)
    fetchDomains()
  }, 300)
})

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + domains.value.length - 1, pagination.total.value))

function formatDate(ts: number): string {
  return DateTime.fromSeconds(ts).toFormat('dd.MM.yyyy')
}

onMounted(() => {
  fetchDomains()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.kubernetes.domains.title')"
    :subtitle="$t('dashboard.admin.kubernetes.domains.subtitle')"
    icon="mdi-web"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminK8sHome' })">
        <v-icon icon="mdi-arrow-left" size="16" />
        {{ $t('dashboard.admin.kubernetes.title') }}
      </button>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('dashboard.admin.kubernetes.domains.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <div class="status-filters">
        <button
          class="filter-chip"
          :class="{ active: verifiedFilter === 'all' }"
          @click="verifiedFilter = 'all'"
        >
          {{ $t('dashboard.admin.kubernetes.domains.filter.all') }}
        </button>
        <button
          class="filter-chip"
          :class="{ active: verifiedFilter === 'verified' }"
          @click="verifiedFilter = 'verified'"
        >
          {{ $t('dashboard.admin.kubernetes.domains.filter.verified') }}
        </button>
        <button
          class="filter-chip"
          :class="{ active: verifiedFilter === 'pending' }"
          @click="verifiedFilter = 'pending'"
        >
          {{ $t('dashboard.admin.kubernetes.domains.filter.pending') }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <!-- Empty State -->
    <div v-else-if="domains.length === 0" class="state-box">
      <v-icon icon="mdi-web-off" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.admin.kubernetes.domains.empty.title') }}</p>
      <p class="empty-hint">{{ $t('dashboard.admin.kubernetes.domains.empty.description') }}</p>
    </div>

    <!-- Domains Table -->
    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header domain-grid">
          <span class="col">{{ $t('dashboard.admin.kubernetes.domains.table.domain') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.domains.table.account') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.domains.table.verified') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.domains.table.created') }}</span>
        </div>
        <div
          v-for="d in domains"
          :key="d.id"
          class="table-row domain-grid"
        >
          <span class="col mono">{{ d.domain }}</span>
          <span class="col account-cell">
            <span class="account-email">{{ d.account_email }}</span>
            <span class="account-id">#{{ d.account_id }}</span>
          </span>
          <span class="col">
            <span class="status-chip" :class="d.verified ? 'status-success' : 'status-warning'">
              {{ d.verified ? $t('dashboard.admin.kubernetes.domains.verifiedLabel') : $t('dashboard.admin.kubernetes.domains.pendingLabel') }}
            </span>
          </span>
          <span class="col date-cell">{{ formatDate(d.created_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ $t('dashboard.admin.kubernetes.domains.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.goToPage(1)">
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.prevPage()">
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">{{ pagination.page.value }} / {{ pagination.totalPages.value }}</span>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.nextPage()">
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.goToPage(pagination.totalPages.value)">
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 280px;
  flex: 1;
}

.status-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }

  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;
  }
}

.btn-text {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
}

.data-table {
  overflow-x: auto;
}

.domain-grid {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  min-width: 700px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  font-weight: 500;
}

.account-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.account-id {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}

@media (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
